import React, { useEffect, useRef, useState, useReducer } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, IconButton, Alert, Slider, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { deleteImage, getImage, getImageFile, listImages, verifyImage } from '../../api/annotatorImages';
import { getAnnotations, saveAnnotations } from '../../api/annotatorAnnotations';
import { getProject } from '../../api/annotatorProjects';
import { inferProjectImage } from '../../api/annotatorPipeline';
import type { AnnotatorProject, AnnotatorImageItem, Label } from '../../types/annotator';

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move';
const HANDLE_HIT_PX = 8;

interface DrawBox {
  id?: number;
  label_id: number;
  x_center: number;
  y_center: number;
  width: number;
  height: number;
}

type Filter = 'all' | 'annotated' | 'unannotated' | 'unverified';

interface EditorLocationState {
  imageIds?: number[];
  page?: number;
  pageSize?: number;
  filter?: Filter;
  labelId?: number;
  pages?: number;
}

function getBoxPixels(box: DrawBox, iw: number, ih: number) {
  const x = (box.x_center - box.width / 2) * iw;
  const y = (box.y_center - box.height / 2) * ih;
  return { x, y, w: box.width * iw, h: box.height * ih };
}

function getHandlePositions(box: DrawBox, iw: number, ih: number): Partial<Record<Handle, { x: number; y: number }>> {
  const { x, y, w, h } = getBoxPixels(box, iw, ih);
  return {
    nw: { x, y }, n: { x: x + w / 2, y }, ne: { x: x + w, y },
    e: { x: x + w, y: y + h / 2 },
    se: { x: x + w, y: y + h }, s: { x: x + w / 2, y: y + h }, sw: { x, y: y + h },
    w: { x, y: y + h / 2 },
  };
}

function hitTestHandles(imgX: number, imgY: number, box: DrawBox, iw: number, ih: number, r: number): Handle | null {
  for (const [name, pos] of Object.entries(getHandlePositions(box, iw, ih)) as [Handle, { x: number; y: number }][]) {
    if (Math.abs(imgX - pos.x) <= r && Math.abs(imgY - pos.y) <= r) return name;
  }
  const { x, y, w, h } = getBoxPixels(box, iw, ih);
  if (imgX >= x && imgX <= x + w && imgY >= y && imgY <= y + h) return 'move';
  return null;
}

function applyHandleDrag(original: DrawBox, handle: Handle, dx: number, dy: number, iw: number, ih: number): DrawBox {
  let left = (original.x_center - original.width / 2) * iw;
  let top = (original.y_center - original.height / 2) * ih;
  let right = left + original.width * iw;
  let bottom = top + original.height * ih;
  if (handle === 'nw' || handle === 'w' || handle === 'sw') left += dx;
  if (handle === 'ne' || handle === 'e' || handle === 'se') right += dx;
  if (handle === 'nw' || handle === 'n' || handle === 'ne') top += dy;
  if (handle === 'sw' || handle === 's' || handle === 'se') bottom += dy;
  if (handle === 'move') { left += dx; right += dx; top += dy; bottom += dy; }
  if (right - left < 5) right = left + 5;
  if (bottom - top < 5) bottom = top + 5;
  left = Math.max(0, left); top = Math.max(0, top);
  right = Math.min(iw, right); bottom = Math.min(ih, bottom);
  return { ...original, x_center: (left + right) / 2 / iw, y_center: (top + bottom) / 2 / ih, width: (right - left) / iw, height: (bottom - top) / ih };
}

const CURSOR_MAP: Record<Handle, string> = {
  nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize', e: 'e-resize',
  se: 'se-resize', s: 's-resize', sw: 'sw-resize', w: 'w-resize', move: 'move',
};

function clampInferConfidence(value: number) {
  if (!Number.isFinite(value)) return 0.5;
  return Math.min(1, Math.max(0.01, value));
}

export default function AnnotatorEditorPage() {
  const { projectId, imageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const loadSeqRef = useRef(0);

  const [project, setProject] = useState<AnnotatorProject | null>(null);
  const [image, setImage] = useState<AnnotatorImageItem | null>(null);
  const [boxes, setBoxes] = useState<DrawBox[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);
  const [selectedBox, setSelectedBox] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [autoInferring, setAutoInferring] = useState(false);
  const [inferMessage, setInferMessage] = useState<{ severity: 'success' | 'info' | 'error'; text: string } | null>(null);
  const [inferConfidence, setInferConfidence] = useState(0.5);
  const [pageImageIds, setPageImageIds] = useState<number[]>([]);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const locationState = (location.state as EditorLocationState | null) ?? null;
  const filter = (searchParams.get('filter') as Filter | null) ?? locationState?.filter ?? 'all';
  const page = Number(searchParams.get('page') ?? locationState?.page ?? 1);
  const pageSize = Number(searchParams.get('pageSize') ?? locationState?.pageSize ?? 60);
  const parsedLabelId = Number(searchParams.get('labelId') ?? locationState?.labelId ?? '');
  const labelId = Number.isFinite(parsedLabelId) && parsedLabelId > 0 ? parsedLabelId : undefined;

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const drawing = useRef(false);
  const drawStart = useRef({ x: 0, y: 0 });
  const currentRect = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const panning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOffset = useRef({ x: 0, y: 0 });
  const resizing = useRef<{ boxIndex: number; handle: Handle; startX: number; startY: number; original: DrawBox } | null>(null);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  // Load data
  useEffect(() => {
    if (!projectId || !imageId) return;
    drawing.current = false; currentRect.current = null; panning.current = false; resizing.current = null;
    setSelectedBox(null); setSaved(false);
    const loadSeq = ++loadSeqRef.current;

    Promise.all([
      getProject(Number(projectId)),
      getImage(Number(projectId), Number(imageId)),
      getAnnotations(Number(projectId), Number(imageId)),
    ]).then(([proj, img, anns]) => {
      if (loadSeq !== loadSeqRef.current) return;
      setProject(proj); setImage(img);
      setInferConfidence(clampInferConfidence(proj.inference_confidence ?? 0.5));
      setInferMessage(null);
      setBoxes(anns.map((a) => ({ id: a.id, label_id: a.label_id, x_center: a.x_center, y_center: a.y_center, width: a.width, height: a.height })));
      setSelectedLabel((prev) => {
        if (prev) { const m = proj.labels.find((l) => l.id === prev.id); if (m) return m; }
        return proj.labels.length > 0 ? proj.labels[0] : null;
      });

      getImageFile(Number(projectId), Number(imageId)).then((blobUrl) => {
        if (loadSeq !== loadSeqRef.current) { URL.revokeObjectURL(blobUrl); return; }
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = blobUrl;
        const htmlImg = new Image();
        htmlImg.src = blobUrl;
        htmlImg.onload = () => {
          if (loadSeq !== loadSeqRef.current) return;
          imgRef.current = htmlImg;
          if (canvasRef.current && canvasRef.current.clientWidth > 0) fitToCanvas(htmlImg);
        };
      });
    }).catch(console.error);
  }, [projectId, imageId]);

  // Page IDs for nav
  useEffect(() => {
    if (!projectId || !imageId) return;
    const routeImageId = Number(imageId);
    const stateImageIds = locationState?.imageIds ?? [];
    if (stateImageIds.includes(routeImageId)) {
      setPageImageIds(stateImageIds); setResolvedPage(locationState?.page ?? page); setPageCount(locationState?.pages ?? 1);
      return;
    }
    const annotated = filter === 'all' ? undefined : filter === 'annotated';
    listImages(Number(projectId), annotated, page, pageSize, labelId)
      .then(async (result) => {
        const ids = result.items.map((i) => i.id);
        if (ids.includes(routeImageId)) { setPageImageIds(ids); setResolvedPage(page); setPageCount(result.pages); return; }
        for (let cp = 1; cp <= result.pages; cp++) {
          if (cp === page) continue;
          const c = await listImages(Number(projectId), annotated, cp, pageSize, labelId);
          const cIds = c.items.map((i) => i.id);
          if (cIds.includes(routeImageId)) { setPageImageIds(cIds); setResolvedPage(cp); setPageCount(c.pages); return; }
        }
        setPageImageIds(ids); setResolvedPage(page); setPageCount(result.pages);
      }).catch(console.error);
  }, [projectId, imageId, filter, page, pageSize, labelId, locationState]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => { if (imgRef.current) fitToCanvas(imgRef.current); });
    ro.observe(container); return () => ro.disconnect();
  }, []);

  useEffect(() => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); }, []);

  function fitToCanvas(img: HTMLImageElement) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cw = canvas.clientWidth || canvas.parentElement?.clientWidth || 800;
    const ch = canvas.clientHeight || canvas.parentElement?.clientHeight || 600;
    const s = Math.min(cw / img.naturalWidth, ch / img.naturalHeight, 1);
    setScale(s);
    setOffset({ x: (cw - img.naturalWidth * s) / 2, y: (ch - img.naturalHeight * s) / 2 });
  }

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current || !project) return;
    const ctx = canvas.getContext('2d')!;
    canvas.width = canvas.clientWidth; canvas.height = canvas.clientHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save(); ctx.translate(offset.x, offset.y); ctx.scale(scale, scale);
    ctx.drawImage(imgRef.current, 0, 0);

    const iw = image?.width ?? imgRef.current.naturalWidth;
    const ih = image?.height ?? imgRef.current.naturalHeight;

    boxes.forEach((box, i) => {
      const label = project.labels.find((l) => l.id === box.label_id);
      const color = label?.color ?? '#6366f1';
      const px = (box.x_center - box.width / 2) * iw;
      const py = (box.y_center - box.height / 2) * ih;
      const pw = box.width * iw; const ph = box.height * ih;
      ctx.strokeStyle = color;
      ctx.lineWidth = selectedBox === i ? 3 / scale : 1.5 / scale;
      ctx.strokeRect(px, py, pw, ph);
      ctx.fillStyle = color; ctx.globalAlpha = 0.3;
      const fontSize = Math.max(10, 12 / scale);
      ctx.font = `${fontSize}px sans-serif`;
      const text = label?.name ?? `Label ${box.label_id}`;
      const tw = ctx.measureText(text).width;
      ctx.fillRect(px, py - fontSize - 4 / scale, tw + 8 / scale, fontSize + 6 / scale);
      ctx.globalAlpha = 1; ctx.fillStyle = color;
      ctx.fillText(text, px + 4 / scale, py - 3 / scale);
    });

    if (selectedBox !== null && boxes[selectedBox]) {
      const hb = boxes[selectedBox]; const hs = 5 / scale;
      ctx.lineWidth = 1 / scale;
      for (const pos of Object.values(getHandlePositions(hb, iw, ih)) as { x: number; y: number }[]) {
        ctx.fillStyle = '#ffffff'; ctx.fillRect(pos.x - hs, pos.y - hs, hs * 2, hs * 2);
        ctx.strokeStyle = '#333333'; ctx.strokeRect(pos.x - hs, pos.y - hs, hs * 2, hs * 2);
      }
    }

    const rect = currentRect.current;
    if (rect && selectedLabel) {
      ctx.strokeStyle = selectedLabel.color; ctx.lineWidth = 1.5 / scale;
      ctx.setLineDash([6 / scale, 3 / scale]); ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
      ctx.setLineDash([]);
    }
    ctx.restore();
  });

  function canvasToImage(cx: number, cy: number) {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    return { x: ((cx - r.left) - offset.x) / scale, y: ((cy - r.top) - offset.y) / scale };
  }

  function onMouseDown(e: React.MouseEvent) {
    if (!imgRef.current || !image) return;
    const { x, y } = canvasToImage(e.clientX, e.clientY);
    const iw = image.width; const ih = image.height;
    if (e.button === 1 || e.altKey) {
      panning.current = true; panStart.current = { x: e.clientX, y: e.clientY }; panOffset.current = { ...offset };
      return;
    }
    const hitRadius = HANDLE_HIT_PX / scale;
    const order = selectedBox !== null ? [selectedBox, ...boxes.map((_, i) => i).filter(i => i !== selectedBox)] : boxes.map((_, i) => i).reverse();
    for (const i of order) {
      const handle = hitTestHandles(x, y, boxes[i], iw, ih, hitRadius);
      if (handle) { setSelectedBox(i); resizing.current = { boxIndex: i, handle, startX: x, startY: y, original: { ...boxes[i] } }; return; }
    }
    if (selectedLabel) { drawing.current = true; drawStart.current = { x, y }; currentRect.current = { x, y, w: 0, h: 0 }; }
    setSelectedBox(null);
  }

  function onMouseMove(e: React.MouseEvent) {
    if (panning.current) {
      setOffset({ x: panOffset.current.x + (e.clientX - panStart.current.x), y: panOffset.current.y + (e.clientY - panStart.current.y) });
      return;
    }
    const { x, y } = canvasToImage(e.clientX, e.clientY);
    if (resizing.current && image) {
      const { startX, startY, original, handle, boxIndex } = resizing.current;
      setBoxes(prev => prev.map((b, i) => i === boxIndex ? applyHandleDrag(original, handle, x - startX, y - startY, image.width, image.height) : b));
      return;
    }
    if (drawing.current) {
      currentRect.current = { x: Math.min(drawStart.current.x, x), y: Math.min(drawStart.current.y, y), w: Math.abs(x - drawStart.current.x), h: Math.abs(y - drawStart.current.y) };
      forceUpdate(); return;
    }
    if (!image) return;
    const iw = image.width; const ih = image.height; const hitRadius = HANDLE_HIT_PX / scale;
    const order = selectedBox !== null ? [selectedBox, ...boxes.map((_, i) => i).filter(i => i !== selectedBox)] : boxes.map((_, i) => i).reverse();
    for (const i of order) {
      const handle = hitTestHandles(x, y, boxes[i], iw, ih, hitRadius);
      if (handle) { if (canvasRef.current) canvasRef.current.style.cursor = CURSOR_MAP[handle]; return; }
    }
    if (canvasRef.current) canvasRef.current.style.cursor = selectedLabel ? 'crosshair' : 'default';
  }

  function onMouseUp() {
    if (panning.current) { panning.current = false; return; }
    if (resizing.current) { resizing.current = null; setSaved(false); return; }
    if (!drawing.current || !imgRef.current || !image || !selectedLabel) return;
    drawing.current = false;
    const rect = currentRect.current; currentRect.current = null;
    if (!rect || rect.w < 5 || rect.h < 5) return;
    const iw = image.width; const ih = image.height;
    const clamped: DrawBox = {
      label_id: selectedLabel.id,
      x_center: Math.min(1, Math.max(0, (rect.x + rect.w / 2) / iw)),
      y_center: Math.min(1, Math.max(0, (rect.y + rect.h / 2) / ih)),
      width: Math.min(1, Math.max(0, rect.w / iw)),
      height: Math.min(1, Math.max(0, rect.h / ih)),
    };
    setBoxes((prev) => [...prev, clamped]); setSaved(false);
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    const mx = e.clientX - r.left; const my = e.clientY - r.top;
    setScale((s) => {
      const ns = Math.min(10, Math.max(0.1, s * factor));
      setOffset((o) => ({ x: mx - (mx - o.x) * (ns / s), y: my - (my - o.y) * (ns / s) }));
      return ns;
    });
  }

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedBox !== null) { setBoxes((prev) => prev.filter((_, i) => i !== selectedBox)); setSelectedBox(null); setSaved(false); }
      }
      if (e.key === 'z' && (e.ctrlKey || e.metaKey)) { setBoxes((prev) => prev.slice(0, -1)); setSaved(false); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedBox]);

  async function persistAnnotations() {
    if (!projectId || !imageId) return;
    const saved_boxes = await saveAnnotations(Number(projectId), Number(imageId), boxes);
    setBoxes(saved_boxes.map((b) => ({ id: b.id, label_id: b.label_id, x_center: b.x_center, y_center: b.y_center, width: b.width, height: b.height })));
    setSaved(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await persistAnnotations();
    } finally { setSaving(false); }
  }

  async function handleDeleteImage() {
    if (!projectId || !imageId || !image) return;
    if (!confirm(`Delete "${image.original_filename}"?`)) return;
    setDeleting(true);
    try { await deleteImage(Number(projectId), Number(imageId)); navigate(backToListHref); }
    finally { setDeleting(false); }
  }

  async function handleVerify() {
    if (!projectId || !imageId || !image) return;
    setVerifying(true);
    try {
      setSaving(true);
      await persistAnnotations();
      const updated = await verifyImage(Number(projectId), Number(imageId));
      setImage(updated);
    } finally {
      setSaving(false);
      setVerifying(false);
    }
  }

  async function handleAutoInferImage() {
    if (!projectId || !imageId || !project) return;
    setAutoInferring(true);
    setInferMessage(null);
    try {
      const result = await inferProjectImage(Number(projectId), Number(imageId), inferConfidence);
      setImage(result.image as AnnotatorImageItem);
      setBoxes(result.annotations.map((a) => ({
        id: a.id,
        label_id: a.label_id,
        x_center: a.x_center,
        y_center: a.y_center,
        width: a.box_width,
        height: a.box_height,
      })));
      setSaved(true);
      setSelectedBox(null);
      setInferMessage(
        result.applied
          ? { severity: 'success', text: `Applied ${result.annotations.length} inferred annotation${result.annotations.length !== 1 ? 's' : ''}.` }
          : { severity: 'info', text: 'No detections found for this image. Existing annotations were left unchanged.' },
      );
    } catch (err: any) {
      setInferMessage({ severity: 'error', text: err.message || 'Auto inference failed.' });
    } finally {
      setAutoInferring(false);
    }
  }

  // Nav
  const currentImageId = Number(imageId);
  const currentIndex = pageImageIds.indexOf(currentImageId);
  const listSearchParams = new URLSearchParams({ filter, page: String(resolvedPage), pageSize: String(pageSize) });
  if (labelId !== undefined) listSearchParams.set('labelId', String(labelId));
  const backToListHref = `/annotator/projects/${projectId}/images?${listSearchParams.toString()}`;

  async function navigateToImage(direction: 'prev' | 'next') {
    if (!projectId || !imageId) return;
    const step = direction === 'next' ? 1 : -1;
    const adjacentId = currentIndex >= 0 ? pageImageIds[currentIndex + step] : undefined;
    if (adjacentId) {
      navigate(`/annotator/projects/${projectId}/images/${adjacentId}?${listSearchParams.toString()}`,
        { state: { imageIds: pageImageIds, page: resolvedPage, pageSize, filter, labelId, pages: pageCount } });
      return;
    }
    const adjacentPage = resolvedPage + step;
    if (adjacentPage < 1 || adjacentPage > pageCount) return;
    const annotated = filter === 'all' ? undefined : filter === 'annotated';
    const result = await listImages(Number(projectId), annotated, adjacentPage, pageSize, labelId);
    const targetId = direction === 'next' ? result.items[0]?.id : result.items[result.items.length - 1]?.id;
    if (!targetId) return;
    navigate(`/annotator/projects/${projectId}/images/${targetId}?filter=${filter}&page=${adjacentPage}&pageSize=${pageSize}${labelId !== undefined ? `&labelId=${labelId}` : ''}`,
      { state: { imageIds: result.items.map((i) => i.id), page: adjacentPage, pageSize, filter, labelId, pages: result.pages } });
  }

  const hasPrev = currentIndex > 0 || resolvedPage > 1;
  const hasNext = (currentIndex >= 0 && currentIndex < pageImageIds.length - 1) || resolvedPage < pageCount;

  if (!image || !project) return <Typography color="text.secondary" sx={{ p: 4 }}>Loading…</Typography>;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Paper sx={{ width: 260, minWidth: 260, display: 'flex', flexDirection: 'column', borderRadius: 0, p: 2, overflowY: 'auto', overflowX: 'hidden' }}>
        <Button component={RouterLink} to={backToListHref} size="small" sx={{ mb: 1, justifyContent: 'flex-start' }}>← Images</Button>
        <Typography variant="subtitle2" noWrap title={image.original_filename} sx={{ mb: 1 }}>{image.original_filename}</Typography>

        {!image.is_verified && (
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
            AI Generated – Unverified
          </Alert>
        )}
        {image.is_verified && image.source === 'auto_inference' && (
          <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
            AI – Verified
          </Alert>
        )}
        {inferMessage && (
          <Alert severity={inferMessage.severity} sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
            {inferMessage.text}
          </Alert>
        )}

        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>Label</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          {project.labels.map((l) => (
            <Button
              key={l.id}
              size="small"
              variant={selectedLabel?.id === l.id ? 'contained' : 'text'}
              onClick={() => setSelectedLabel(l)}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              startIcon={<Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: l.color, flexShrink: 0 }} />}
            >
              {l.name}
            </Button>
          ))}
          {project.labels.length === 0 && <Typography variant="caption" color="text.secondary">Add labels in Project settings first.</Typography>}
        </Box>

        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>Boxes ({boxes.length})</Typography>
        <Box sx={{ mb: 2 }}>
          {boxes.map((box, i) => {
            const label = project.labels.find((l) => l.id === box.label_id);
            return (
              <Box
                key={i}
                onClick={() => setSelectedBox(i)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                  bgcolor: selectedBox === i ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: label?.color ?? '#888', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>{label?.name ?? `Label ${box.label_id}`}</Typography>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setBoxes((prev) => prev.filter((_, j) => j !== i)); setSaved(false); }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
          <IconButton size="small" onClick={() => navigateToImage('prev')} disabled={!hasPrev || saving}><ArrowBackIcon /></IconButton>
          <Button variant="contained" fullWidth onClick={handleSave} disabled={saving} size="small">
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
          </Button>
          <IconButton size="small" onClick={() => navigateToImage('next')} disabled={!hasNext || saving}><ArrowForwardIcon /></IconButton>
        </Box>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={handleAutoInferImage}
          disabled={autoInferring || saving}
          sx={{ mb: 1 }}
        >
          {autoInferring ? 'Inferencing…' : 'Auto Infer This Image'}
        </Button>
        {!image.is_verified && (
          <Button
            variant="contained"
            color="warning"
            size="small"
            fullWidth
            startIcon={verifying ? undefined : <CheckCircleIcon />}
            onClick={handleVerify}
            disabled={verifying || saving}
            sx={{ mb: 1 }}
          >
            {verifying ? 'Verifying…' : '✓ Accept & Verify'}
          </Button>
        )}
        <Button size="small" color="error" onClick={handleDeleteImage} disabled={deleting || saving} fullWidth>
          {deleting ? 'Deleting…' : 'Delete Image'}
        </Button>

        <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.8 }}>
            <code>Drag</code> Draw box<br />
            <code>Drag box</code> Move<br />
            <code>Drag handle</code> Resize<br />
            <code>Alt+Drag</code> Pan<br />
            <code>Scroll</code> Zoom<br />
            <code>Del</code> Delete selected<br />
            <code>Ctrl+Z</code> Undo last
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Auto Infer Confidence: <strong>{inferConfidence.toFixed(2)}</strong>
          </Typography>
          <Slider
            value={inferConfidence}
            onChange={(_, value) => setInferConfidence(clampInferConfidence(value as number))}
            min={0.01}
            max={1}
            step={0.01}
            marks={[{ value: 0.1, label: '0.1' }, { value: 0.2, label: '0.2' }, { value: 0.5, label: '0.5' }]}
            size="small"
            disabled={autoInferring || saving}
          />
          <TextField
            size="small"
            fullWidth
            label="Confidence"
            type="number"
            value={inferConfidence}
            onChange={(event) => setInferConfidence(clampInferConfidence(Number(event.target.value)))}
            inputProps={{ min: 0.01, max: 1, step: 0.01 }}
            disabled={autoInferring || saving}
            helperText="Used for Auto Infer This Image only. Project default is on the project settings page."
          />
        </Box>
      </Paper>

      {/* Canvas */}
      <Box ref={containerRef} sx={{ flex: 1, position: 'relative', bgcolor: '#1a1a2e' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', display: 'block' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => { if (canvasRef.current) canvasRef.current.style.cursor = 'default'; }}
          onWheel={onWheel}
        />
      </Box>
    </Box>
  );
}
